import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, ShoppingBag, Star, ChevronLeft, Package, Shield, Truck } from 'lucide-react';
import { productAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import ProductCard from '../../components/shop/ProductCard';

const ProductPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImg, setSelectedImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('description');
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    setLoading(true);
    productAPI.getOne(id)
      .then(({ data }) => {
        setProduct(data);
        setSelectedSize(data.size?.[0] || '');
        setSelectedImg(0);
        // fetch related
        return productAPI.getAll({ category: data.category, limit: 4 });
      })
      .then(({ data }) => setRelated(data.products.filter(p => p._id !== id).slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = () => addToCart(product._id, qty, selectedSize);

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login to review'); return; }
    setSubmittingReview(true);
    try {
      await productAPI.addReview(id, review);
      toast.success('Review submitted!');
      const { data } = await productAPI.getOne(id);
      setProduct(data);
      setReview({ rating: 5, comment: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally { setSubmittingReview(false); }
  };

  if (loading) return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '48px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px' }}>
          <div className="skeleton" style={{ aspectRatio: '1' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[100, 60, 40, 80, 50].map((w, i) => <div key={i} className="skeleton" style={{ height: '24px', width: `${w}%` }} />)}
          </div>
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div style={{ paddingTop: '72px', textAlign: 'center', padding: '120px 24px', color: 'var(--white-dim)' }}>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: '32px' }}>Product not found</p>
      <Link to="/collection" style={{ color: 'var(--gold)', marginTop: '16px', display: 'inline-block' }}>← Back to Collection</Link>
    </div>
  );

  const price = product.salePrice || product.price;
  const hasDiscount = product.salePrice && product.salePrice < product.price;
  const wishlisted = isWishlisted(product._id);

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '80px' }}>
        {/* Breadcrumb */}
        <Link to="/collection" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--white-dim)', letterSpacing: '0.1em', marginBottom: '40px' }}>
          <ChevronLeft size={12} /> COLLECTION
        </Link>

        {/* Main */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', marginBottom: '80px' }}>
          {/* Images */}
          <div>
            <div style={{ aspectRatio: '1', background: 'var(--dark-2)', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '16px' }}>
              {product.images?.length > 0 ? (
                <img src={product.images[selectedImg]} alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--white-faint)', fontFamily: 'var(--font-display)', fontSize: '18px' }}>NO IMAGE</div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImg(i)}
                    style={{ width: '80px', height: '80px', border: `2px solid ${selectedImg === i ? 'var(--gold)' : 'var(--border)'}`, overflow: 'hidden', cursor: 'pointer', background: 'none', transition: 'border-color 0.3s' }}>
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p style={{ fontSize: '11px', color: 'var(--gold)', letterSpacing: '0.25em', marginBottom: '12px', fontWeight: 500 }}>{product.brand?.toUpperCase()}</p>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400, lineHeight: 1.15, marginBottom: '16px' }}>{product.name}</h1>

            {/* Rating */}
            {product.numReviews > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                {[1,2,3,4,5].map(s => <Star key={s} size={14} fill={s <= Math.round(product.rating) ? 'var(--gold)' : 'none'} color={s <= Math.round(product.rating) ? 'var(--gold)' : 'var(--border)'} />)}
                <span style={{ fontSize: '12px', color: 'var(--white-dim)' }}>{product.rating.toFixed(1)} ({product.numReviews} reviews)</span>
              </div>
            )}

            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '32px' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '36px', color: hasDiscount ? 'var(--gold)' : 'var(--white)' }}>${price}</span>
              {hasDiscount && <span style={{ fontSize: '20px', color: 'var(--white-faint)', textDecoration: 'line-through' }}>${product.price}</span>}
            </div>

            {/* Badges */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
              {[product.concentration, product.gender, product.category].filter(Boolean).map(badge => (
                <span key={badge} style={{ fontSize: '10px', border: '1px solid var(--border)', padding: '4px 10px', letterSpacing: '0.1em', color: 'var(--white-dim)', textTransform: 'capitalize' }}>{badge}</span>
              ))}
            </div>

            {/* Size */}
            {product.size?.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '11px', letterSpacing: '0.15em', marginBottom: '10px', color: 'var(--white-dim)' }}>SIZE</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {product.size.map(s => (
                    <button key={s} onClick={() => setSelectedSize(s)}
                      style={{ padding: '8px 16px', border: `1px solid ${selectedSize === s ? 'var(--gold)' : 'var(--border)'}`, background: selectedSize === s ? 'var(--gold)' : 'transparent', color: selectedSize === s ? 'var(--black)' : 'var(--white)', fontSize: '12px', cursor: 'pointer', transition: 'all 0.3s' }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Qty */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)' }}>
                <button onClick={() => setQty(Math.max(1, qty - 1))} style={{ width: '40px', height: '40px', background: 'none', color: 'var(--white)', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <span style={{ width: '40px', textAlign: 'center', fontSize: '14px' }}>{qty}</span>
                <button onClick={() => setQty(Math.min(product.countInStock, qty + 1))} style={{ width: '40px', height: '40px', background: 'none', color: 'var(--white)', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              </div>
              <span style={{ fontSize: '12px', color: product.countInStock > 0 ? 'var(--gold)' : '#ef4444' }}>
                {product.countInStock > 0 ? `${product.countInStock} in stock` : 'Out of stock'}
              </span>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
              <button onClick={handleAddToCart} disabled={product.countInStock === 0}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'var(--gold)', color: 'var(--black)', padding: '14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', cursor: product.countInStock === 0 ? 'not-allowed' : 'pointer', opacity: product.countInStock === 0 ? 0.5 : 1, transition: 'background 0.3s' }}
                onMouseEnter={e => { if (product.countInStock > 0) e.currentTarget.style.background = 'var(--gold-light)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--gold)'}
              ><ShoppingBag size={14} /> ADD TO CART</button>
              <button onClick={() => toggleWishlist(product._id)}
                style={{ width: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${wishlisted ? 'var(--gold)' : 'var(--border)'}`, background: 'transparent', color: wishlisted ? 'var(--gold)' : 'var(--white-dim)', cursor: 'pointer', transition: 'all 0.3s' }}>
                <Heart size={18} fill={wishlisted ? 'var(--gold)' : 'none'} />
              </button>
            </div>

            {/* Trust badges */}
            <div style={{ display: 'flex', gap: '24px', borderTop: '1px solid var(--border)', paddingTop: '24px' }}>
              {[{ icon: Truck, text: 'Free shipping over $200' }, { icon: Shield, text: 'Authentic guarantee' }, { icon: Package, text: 'Luxury packaging' }].map(({ icon: Icon, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: 'var(--white-dim)' }}>
                  <Icon size={14} color="var(--gold)" /> {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--border)', marginBottom: '32px', display: 'flex', gap: '32px' }}>
          {['description', 'notes', 'reviews'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ background: 'none', color: tab === t ? 'var(--gold)' : 'var(--white-dim)', fontSize: '11px', letterSpacing: '0.2em', fontWeight: 600, paddingBottom: '16px', borderBottom: `2px solid ${tab === t ? 'var(--gold)' : 'transparent'}`, textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s' }}>
              {t}{t === 'reviews' ? ` (${product.numReviews})` : ''}
            </button>
          ))}
        </div>

        {tab === 'description' && (
          <div style={{ maxWidth: '700px', fontSize: '14px', color: 'var(--white-dim)', lineHeight: 1.9, letterSpacing: '0.03em' }}>
            {product.description}
          </div>
        )}

        {tab === 'notes' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px', maxWidth: '700px' }}>
            {[['Top Notes', product.notes?.top], ['Heart Notes', product.notes?.middle], ['Base Notes', product.notes?.base]].map(([label, notes]) => (
              <div key={label}>
                <h4 style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--gold)', marginBottom: '12px', fontWeight: 600 }}>{label.toUpperCase()}</h4>
                {notes?.length > 0 ? notes.map(n => (
                  <p key={n} style={{ fontSize: '13px', color: 'var(--white-dim)', marginBottom: '6px' }}>{n}</p>
                )) : <p style={{ fontSize: '13px', color: 'var(--white-faint)' }}>—</p>}
              </div>
            ))}
          </div>
        )}

        {tab === 'reviews' && (
          <div style={{ maxWidth: '700px' }}>
            {product.reviews?.length === 0 ? (
              <p style={{ color: 'var(--white-dim)', fontSize: '14px', marginBottom: '32px' }}>No reviews yet. Be the first!</p>
            ) : (
              <div style={{ marginBottom: '40px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {product.reviews.map(r => (
                  <div key={r._id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[1,2,3,4,5].map(s => <Star key={s} size={12} fill={s <= r.rating ? 'var(--gold)' : 'none'} color={s <= r.rating ? 'var(--gold)' : 'var(--border)'} />)}
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 500 }}>{r.name}</span>
                      <span style={{ fontSize: '11px', color: 'var(--white-faint)' }}>{new Date(r.createdAt).toLocaleDateString()}</span>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--white-dim)', lineHeight: 1.7 }}>{r.comment}</p>
                  </div>
                ))}
              </div>
            )}

            {user && (
              <form onSubmit={handleReview} style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', marginBottom: '20px' }}>Write a Review</h3>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', letterSpacing: '0.15em', display: 'block', marginBottom: '8px', color: 'var(--white-dim)' }}>RATING</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {[1,2,3,4,5].map(s => (
                      <button key={s} type="button" onClick={() => setReview(r => ({ ...r, rating: s }))}
                        style={{ background: 'none', cursor: 'pointer', padding: 0 }}>
                        <Star size={20} fill={s <= review.rating ? 'var(--gold)' : 'none'} color={s <= review.rating ? 'var(--gold)' : 'var(--border)'} />
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '11px', letterSpacing: '0.15em', display: 'block', marginBottom: '8px', color: 'var(--white-dim)' }}>YOUR REVIEW</label>
                  <textarea value={review.comment} onChange={e => setReview(r => ({ ...r, comment: e.target.value }))} required
                    rows={4} style={{ width: '100%', padding: '12px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', resize: 'vertical', lineHeight: 1.7 }} />
                </div>
                <button type="submit" disabled={submittingReview}
                  style={{ background: 'var(--gold)', color: 'var(--black)', padding: '12px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', cursor: submittingReview ? 'not-allowed' : 'pointer', opacity: submittingReview ? 0.7 : 1 }}>
                  {submittingReview ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Related */}
        {related.length > 0 && (
          <div style={{ marginTop: '80px' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 400, marginBottom: '32px' }}>You Might Also Like</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '24px' }}>
              {related.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .product-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </div>
  );
};

export default ProductPage;
